import React, { useState } from 'react';
import { post } from '../../api';
import './ImportWizard.css';

const ImportWizard = ({ type, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        setError('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }

      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dryRun', 'true'); // Start with dry run

      const response = await post(`/contacts/${type}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      setStep(2);

    } catch (err) {
      console.error('Error during import:', err);
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCommitImport = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dryRun', 'false'); // Commit the import

      const response = await post(`/contacts/${type}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      setStep(3);

    } catch (err) {
      console.error('Error committing import:', err);
      setError(err.response?.data?.error || err.message || 'Import commit failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setImportResults(null);
    setError(null);
    onClose();
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Upload File';
      case 2: return 'Preview & Review';
      case 3: return 'Import Complete';
      default: return 'Import Wizard';
    }
  };

  const getContactPreviewName = (item) =>
    item.contactName || item['primaryContact.name'] || '';

  const supplierFormatHeaders = [
    'Record Id',
    'Suppliers Name',
    "Contact Person's Name",
    "Contact Person's Phone Number",
    "Contact Person's Email"
  ];

  const supplierFormatSample = [
    'zcrm_6127723000001388284',
    'Tradeline Logistics',
    'Victor Were',
    '254721843302',
    'victorwere84@gmail.com'
  ];

  const getStepDescription = () => {
    switch (step) {
      case 1: return `Select a CSV or Excel file to import ${type}`;
      case 2: return 'Review the import results and commit if everything looks correct';
      case 3: return 'Import has been completed successfully';
      default: return '';
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content import-wizard" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getStepTitle()}</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="wizard-content">
          <div className="step-indicator">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Upload</span>
            </div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Preview</span>
            </div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Complete</span>
            </div>
          </div>

          <div className="step-description">
            <p>{getStepDescription()}</p>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="upload-step">
              <div className="file-upload-area">
                <input
                  type="file"
                  id="file-upload"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <label htmlFor="file-upload" className="file-upload-label">
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    <strong>Choose a file</strong> or drag and drop
                  </div>
                  <div className="upload-subtext">
                    CSV or Excel files up to 10MB
                  </div>
                </label>
              </div>

              {file && (
                <div className="file-selected">
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              )}

              <div className="expected-format">
                <h4>Expected CSV/Excel Format:</h4>
                <div className="format-example">
                  <table>
                    <thead>
                      <tr>
                        {(type === 'suppliers' ? supplierFormatHeaders : [
                          'recordId',
                          'name',
                          'contactName',
                          'contactPhone',
                          'contactEmail'
                        ]).map((header) => (
                          <th key={header}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {(type === 'suppliers' ? supplierFormatSample : [
                          'SUP001',
                          'Acme Supplies',
                          'John Doe',
                          '0724 931504',
                          'john@acme.com'
                        ]).map((value) => (
                          <td key={value}>{value}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview Results */}
          {step === 2 && importResults && (
            <div className="preview-step">
              <div className="import-stats">
                <h4>Import Summary</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-number">{importResults.stats?.rows || 0}</span>
                    <span className="stat-label">Total Rows</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{importResults.stats?.create || 0}</span>
                    <span className="stat-label">New Records</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{importResults.stats?.update || 0}</span>
                    <span className="stat-label">Updates</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{importResults.stats?.errors || 0}</span>
                    <span className="stat-label">Errors</span>
                  </div>
                </div>
              </div>

              {importResults.warnings && importResults.warnings.length > 0 && (
                <div className="warnings">
                  <h4>Warnings</h4>
                  <ul>
                    {importResults.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResults.errors && importResults.errors.length > 0 && (
                <div className="errors">
                  <h4>Errors</h4>
                  <ul>
                    {importResults.errors.map((error, index) => (
                      <li key={index}>Row {error.row}: {error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResults.previewSample && importResults.previewSample.length > 0 && (
                <div className="preview-sample">
                  <h4>Preview Sample</h4>
                  <div className="sample-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResults.previewSample.slice(0, 10).map((item, index) => (
                          <tr key={index}>
                            <td>{item.row}</td>
                            <td>{item.name}</td>
                            <td>{getContactPreviewName(item)}</td>
                            <td>
                              <span className={`action-badge ${item.action}`}>
                                {item.action}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Completion */}
          {step === 3 && importResults && (
            <div className="completion-step">
              <div className="success-icon">✅</div>
              <h3>Import Completed Successfully!</h3>
              <div className="final-stats">
                <div className="stat-item">
                  <span className="stat-number">{importResults.stats?.create || 0}</span>
                  <span className="stat-label">New Records Created</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{importResults.stats?.update || 0}</span>
                  <span className="stat-label">Existing Records Updated</span>
                </div>
              </div>
              {importResults.stats?.errors > 0 && (
                <div className="errors-note">
                  <p>{importResults.stats.errors} rows had errors and were skipped.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="wizard-actions">
          {step === 1 && (
            <>
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!file || loading}
              >
                {loading ? 'Processing...' : 'Upload & Preview'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCommitImport}
                disabled={loading || (importResults?.stats?.errors > 0)}
              >
                {loading ? 'Importing...' : 'Commit Import'}
              </button>
            </>
          )}

          {step === 3 && (
            <button 
              className="btn btn-primary"
              onClick={() => {
                onSuccess(`Successfully imported ${importResults.stats?.create || 0} new ${type} and updated ${importResults.stats?.update || 0} existing records.`);
                handleClose();
              }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportWizard;
